// Pure business rules — preserved from the historical VÉRO modules and made explicit.
import { addMonths, diffDays, nextAnnual, type Day } from './dates.ts';
import {
  CASE_MAIN_STAGES, CASE_TERMINAL_STAGES,
  type Case, type CaseRequirement, type CaseStage, type Person, type Policy, type PolicyAdjustment, type Task,
} from './schema.ts';

// --- Money ------------------------------------------------------------------
export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// --- Policies (core v1 → v221 Policy Cockpit) ---------------------------------
/** legacy ACTIVE_STATUSES = ['Active','Renewed','Ενεργό','Ανανεώθηκε'] */
export const isActivePolicy = (p: Pick<Policy, 'status'> & { deletedAt?: string | null }) => !p.deletedAt && (p.status === 'active' || p.status === 'renewed');
/** v13: a soft-deleted (wrongly entered) policy — hidden everywhere, kept for record retention */
export const isPolicyDeleted = (p: { deletedAt?: string | null } | null | undefined) => !!p?.deletedAt;

/** legacy currentAnnual(): base annual + sum of adjustments */
export function currentAnnualPremium(p: Pick<Policy, 'annualPremium'>, adjustments: readonly Pick<PolicyAdjustment, 'annualAmount'>[] = []): number {
  return round2((p.annualPremium || 0) + adjustments.reduce((s, a) => s + (a.annualAmount || 0), 0));
}
export const monthlyFromAnnual = (a: number) => round2(a / 12);
export const annualFromMonthly = (m: number) => round2(m * 12);

/** Next renewal/anniversary date for a policy (explicit renewalDate wins, else start-date anniversary). */
export function nextPolicyAnniversary(p: Pick<Policy, 'renewalDate' | 'startDate'>, today: Day): Day | null {
  if (p.renewalDate && p.renewalDate >= today) return p.renewalDate;
  const anchor = p.renewalDate || p.startDate;
  return anchor ? nextAnnual(anchor, today) : null;
}

// --- Cases / applications (v900 Application Journey) ----------------------------
export const isTerminalStage = (s: CaseStage) => (CASE_TERMINAL_STAGES as readonly string[]).includes(s);
export const isMainStage = (s: CaseStage) => (CASE_MAIN_STAGES as readonly string[]).includes(s);
export function stageIndex(s: CaseStage): number { return (CASE_MAIN_STAGES as readonly string[]).indexOf(s); }
export function nextStage(s: CaseStage): CaseStage | null {
  const i = stageIndex(s);
  return i >= 0 && i < CASE_MAIN_STAGES.length - 1 ? CASE_MAIN_STAGES[i + 1]! : null;
}
/** v900: a case is "stalled" when it has not moved for 7+ days and is not terminal */
export const STALLED_AFTER_DAYS = 7;
export function caseAgeDays(c: Pick<Case, 'updatedAt' | 'createdAt'>, today: Day): number {
  const d = (c.updatedAt || c.createdAt || today).slice(0, 10);
  return Math.max(0, diffDays(d, today));
}
export function isStalled(c: Case, today: Day): boolean {
  return !isTerminalStage(c.stage) && caseAgeDays(c, today) >= STALLED_AFTER_DAYS;
}
export const missingRequirements = (reqs: readonly CaseRequirement[]) => reqs.filter((r) => !r.done);

// --- Reviews (v65 → v603 Review Center 360) --------------------------------------
export const REVIEW_INTERVAL_MONTHS = 12;
export const REVIEW_DUE_SOON_DAYS = 30;
export type ReviewState = 'not_applicable' | 'never' | 'overdue' | 'due_soon' | 'ok';

export interface ReviewStatus { state: ReviewState; dueDate: Day | null; daysOverdue: number; lastReviewDate: Day | null }

/**
 * A relationship needs a review when it holds active policies.
 * Due date = person.nextReviewDate, else lastReviewDate + 12 months,
 * else (never reviewed) the first anniversary of the earliest active policy.
 */
export function reviewStatus(person: Pick<Person, 'lastReviewDate' | 'nextReviewDate' | 'stage'>, policies: readonly Policy[], today: Day): ReviewStatus {
  const active = policies.filter(isActivePolicy);
  const last = person.lastReviewDate ?? null;
  if (!active.length) return { state: 'not_applicable', dueDate: null, daysOverdue: 0, lastReviewDate: last };
  let due: Day | null = person.nextReviewDate ?? (last ? addMonths(last, REVIEW_INTERVAL_MONTHS) : null);
  if (!due) {
    const starts = active.map((p) => p.startDate || p.issueDate).filter((d): d is string => !!d).sort();
    due = starts[0] ? addMonths(starts[0], REVIEW_INTERVAL_MONTHS) : today;
  }
  const delta = diffDays(due, today);
  if (delta > 0) return { state: last ? 'overdue' : 'never', dueDate: due, daysOverdue: delta, lastReviewDate: last };
  if (-delta <= REVIEW_DUE_SOON_DAYS) return { state: 'due_soon', dueDate: due, daysOverdue: 0, lastReviewDate: last };
  return { state: 'ok', dueDate: due, daysOverdue: 0, lastReviewDate: last };
}

// --- Tasks ------------------------------------------------------------------------
export const isOpenTask = (t: Pick<Task, 'status'>) => t.status === 'open';
export function taskDueState(t: Pick<Task, 'status' | 'dueDate'>, today: Day): 'none' | 'overdue' | 'today' | 'upcoming' | 'closed' {
  if (t.status !== 'open') return 'closed';
  if (!t.dueDate) return 'none';
  if (t.dueDate < today) return 'overdue';
  if (t.dueDate === today) return 'today';
  return 'upcoming';
}

// --- People -----------------------------------------------------------------------
export function splitName(full: string): { firstName: string; lastName: string } {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: parts[0] ?? '', lastName: '' };
  return { firstName: parts[0]!, lastName: parts.slice(1).join(' ') };
}
export function initials(name: string): string {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]!.toUpperCase()).join('') || '?';
}
/** accent/case-insensitive search key (Greek + Latin) */
export function searchKey(s: string): string {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}
export function normalizePhone(s: string | null | undefined): string {
  return String(s || '').replace(/[^\d+]/g, '').replace(/^00/, '+');
}
/** digits used for duplicate matching (drops country prefix 357/30 when present) */
export function phoneMatchKey(s: string | null | undefined): string {
  const d = String(s || '').replace(/\D/g, '');
  if (d.length > 8 && (d.startsWith('357') || d.startsWith('30'))) return d.slice(d.startsWith('357') ? 3 : 2);
  return d;
}
